import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, useBreakpointValue, usePrefersReducedMotion } from '@chakra-ui/react';
import { ResizeObserver } from '@juggle/resize-observer';
import Constants from 'Sp/Gallery';
import type {
    IPhoto,
    IPhotoActions,
    IPhotoDimensions,
    IRootScope,
    ISPPhotoService
} from 'ts/client/types';
import PhotoGridItem from './PhotoGridItem';
import { isSlideshowHoverPreviewActive } from './slideshowHoverPreview';
import getPhotoDimensionsMap from './util/getPhotoDimensionsMap';

const layoutTypes = ['hmason', 'vmason'] as const;

type ThumbnailSize = 's' | 'm' | 'l';

interface Props {
    /** Album the grid is showing, when it is an album rather than the gallery home */
    albumId?: number | string | null;
    /** Returns the URL for a given photo and size */
    getPhotoUrl: ISPPhotoService['getUrl'];
    /** Returns the progressive MP4 preview URL for a video */
    getPreviewUrl: ISPPhotoService['getPreviewUrl'];
    /** Returns the 480p hover-preview URL for a video, or null when the event has none */
    getSmallPreviewUrl: ISPPhotoService['getSmallPreviewUrl'];
    /** True only if currently viewing the compare page */
    isComparing: boolean;
    /** True only if the Download button should be visible */
    isDownloadPossible: boolean;
    /** True only if the package builder is open */
    isPackageBuilderOpen: boolean;
    /** Returns whether or not the given photo is selected */
    isPhotoSelected: (photo: IPhoto) => boolean;
    /** True only if two photos have been selected to be compared */
    isReadyToCompare: boolean;
    /** The type of layout to use for rendering the grid's photos */
    layoutType: (typeof layoutTypes)[number];
    /** Called when the given photo is clicked */
    onClickPhoto: (photo: IPhoto) => unknown;
    /** The array of photos to render */
    photos: IPhoto[];
    /** Actions to be performed on photos */
    photoActions: IPhotoActions;
    /** The id of a photo that is part of a pending download that requires the user to select a download destination */
    requiredDestinationSelectionPhotoId?: IPhoto['id'];
    /** Called on mount, to scroll to the target photo */
    scrollToTargetPhoto: () => unknown;
    /** Angular free digitals service */
    spFreeDigitals: SpAngularJs.ISpFreeDigitals;
    /** The size to use for spacing between photos on large screens */
    thumbnailGutterWidth: ThumbnailSize;
    /** The size to use for rendering thumbnails */
    thumbnailSize: ThumbnailSize;
    /** Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
    /** Angular root scope */
    $rootScope: IRootScope;
}

const HOVER_PREVIEW_QUERY = '(hover: hover) and (pointer: fine)';

function useAllowsHoverPreview(): boolean {
    const canMatch = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
    const [allowsHoverPreview, setAllowsHoverPreview] = useState(
        () => canMatch && window.matchMedia(HOVER_PREVIEW_QUERY).matches
    );

    useEffect(() => {
        if (!canMatch) {
            return;
        }

        const query = window.matchMedia(HOVER_PREVIEW_QUERY);
        const update = () => setAllowsHoverPreview(query.matches);

        update();
        query.addEventListener('change', update);

        return () => query.removeEventListener('change', update);
    }, [canMatch]);

    return allowsHoverPreview;
}

const PhotoGrid: FC<Props> = ({
    albumId,
    getPhotoUrl,
    getPreviewUrl,
    getSmallPreviewUrl,
    isComparing,
    isDownloadPossible,
    isPackageBuilderOpen,
    isPhotoSelected,
    isReadyToCompare,
    layoutType,
    onClickPhoto,
    photos,
    photoActions,
    requiredDestinationSelectionPhotoId,
    scrollToTargetPhoto,
    spFreeDigitals,
    thumbnailGutterWidth,
    thumbnailSize,
    translateFilter,
    $rootScope
}) => {
    if (!layoutTypes.includes(layoutType)) {
        throw new Error(`PhotoGrid received unexpected layout type: ${layoutType}`);
    }

    const commonHeight: number =
        useBreakpointValue({
            lg: Constants.CLIENT_THUMBNAIL_SIZE_MAP.HMASON[thumbnailSize]
        }) ?? Constants.CLIENT_THUMBNAIL_SIZES.HMASON.SMALL;
    const commonWidth: number =
        useBreakpointValue({
            lg: Constants.CLIENT_THUMBNAIL_SIZE_MAP.VMASON[thumbnailSize]
        }) ?? Constants.CLIENT_THUMBNAIL_SIZES.VMASON.MEDIUM;
    const padding: number =
        useBreakpointValue({
            lg: Constants.CLIENT_THUMBNAIL_GUTTER_MAP[thumbnailGutterWidth]
        }) ?? Constants.CLIENT_GUTTER_SIZES.SMALL;

    const containerElementRef = useRef<HTMLDivElement>(null);

    const [containerTop, setContainerTop] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [windowHeight, setWindowHeight] = useState(0);
    const [photoDimensionsMap, setPhotoDimensionsMap] = useState<Map<number, IPhotoDimensions>>();
    const [hoveredPreviewPhotoId, setHoveredPreviewPhotoId] =
        useState<Nullable<IPhoto['id']>>(null);

    const isLargeScreen = useBreakpointValue({ lg: true }) ?? false;
    const prefersReducedMotion = usePrefersReducedMotion() ?? false;
    const allowsHoverPreview = useAllowsHoverPreview();

    const windowVirtualTop = useBreakpointValue({ lg: -0.5 * windowHeight }) ?? -windowHeight;
    const windowVirtualBottom = useBreakpointValue({ lg: 1.5 * windowHeight }) ?? 2 * windowHeight;

    const handlePreviewEnter = useCallback((photoId: IPhoto['id']) => {
        setHoveredPreviewPhotoId(photoId);
    }, []);

    const handlePreviewLeave = useCallback((photoId: IPhoto['id']) => {
        // Guarded so a late leave from the previous tile cannot clear a newer tile's hover.
        setHoveredPreviewPhotoId((previousPhotoId) =>
            previousPhotoId === photoId ? null : previousPhotoId
        );
    }, []);

    /**
     * On large screens only the hovered video previews. On smaller screens every video actually on
     * screen previews, so a column showing two of them plays both. Autoplaying looping motion is
     * suppressed entirely for anyone who asked for reduced motion, since the preview deliberately
     * offers no pause control. Slideshows share the hover slot but never use this mobile path.
     */
    const isPreviewActive = (photoId: IPhoto['id'], isOnScreen: boolean) => {
        if (prefersReducedMotion) {
            return false;
        }

        if (isLargeScreen) {
            return hoveredPreviewPhotoId === photoId;
        }

        return isOnScreen;
    };

    const rootHeight = useMemo(() => {
        return photos.reduce((result, photo) => {
            const photoDimensions = photoDimensionsMap?.get(photo.id);

            if (!photoDimensions) {
                return result;
            }

            const height = photoDimensions.top + photoDimensions.height;
            return Math.max(height, result);
        }, 0);
    }, [photos, photoDimensionsMap]);

    // Tracks the container's actual pixel width — nothing else. Recomputing the dimensions map
    // itself is handled by the effect below, keyed on this value alongside photos/layout, so a
    // photos update (e.g. server-provided thumbnail dimensions arriving) recomputes immediately
    // rather than waiting on ResizeObserver's own (real, but incidental) size-change delivery.
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            // Using requestAnimationFrame prevents "Error: ResizeObserver loop completed with undelivered notifications."
            window.requestAnimationFrame(() => {
                if (!containerElementRef.current) {
                    return;
                }

                setContainerWidth(containerElementRef.current.offsetWidth);
            });
        });

        if (containerElementRef.current) {
            resizeObserver.observe(containerElementRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!containerWidth) {
            return;
        }

        const nextPhotoDimensionsMap = getPhotoDimensionsMap({
            commonDimension: layoutType === 'hmason' ? commonHeight : commonWidth,
            containerWidth,
            layoutType,
            padding,
            photos
        });

        setPhotoDimensionsMap(nextPhotoDimensionsMap);
    }, [commonHeight, commonWidth, containerWidth, layoutType, padding, photos]);

    useEffect(() => {
        if (photoDimensionsMap) {
            scrollToTargetPhoto();
        }
    }, [photoDimensionsMap, scrollToTargetPhoto]);

    useEffect(() => {
        const handleWindowResize = () => {
            setWindowHeight(window.innerHeight);
        };

        const handleWindowScroll = () => {
            if (!containerElementRef.current) {
                return;
            }

            const nextContainerTop = containerElementRef.current.getBoundingClientRect().top;
            setContainerTop(nextContainerTop);
        };

        handleWindowResize();
        handleWindowScroll();

        window.addEventListener('resize', handleWindowResize);
        window.addEventListener('scroll', handleWindowScroll);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
            window.removeEventListener('scroll', handleWindowScroll);
        };
    }, []);

    return (
        <Box height={`${rootHeight}px`}>
            <Box ref={containerElementRef} position="relative">
                {photos.map((photo) => {
                    const photoDimensions = photoDimensionsMap?.get(photo.id);

                    if (!photoDimensions) {
                        return null;
                    }

                    const photoActualTop = containerTop + photoDimensions.top;
                    const photoActualBottom = photoActualTop + photoDimensions.height;

                    const topAboveView = photoActualTop < windowVirtualTop;
                    const topBelowView = photoActualTop > windowVirtualBottom;
                    const topInView = !topAboveView && !topBelowView;
                    const bottomAboveView = photoActualBottom < windowVirtualTop;
                    const bottomBelowView = photoActualBottom > windowVirtualBottom;
                    const bottomInView = !bottomAboveView && !bottomBelowView;
                    const isInView = topInView || bottomInView || (topAboveView && bottomBelowView);

                    // The real viewport, unlike isInView's virtual one, which is padded by a
                    // window height on either side so tiles mount before they are scrolled to.
                    const isOnScreen = photoActualBottom > 0 && photoActualTop < windowHeight;

                    return (
                        <PhotoGridItem
                            key={photo.id}
                            albumId={albumId}
                            allowsSlideshowHoverPreview={allowsHoverPreview}
                            getPhotoUrl={getPhotoUrl}
                            getPreviewUrl={getPreviewUrl}
                            getSmallPreviewUrl={getSmallPreviewUrl}
                            isBlackAndWhite={photo.filterBlackAndWhite}
                            isComparing={isComparing}
                            isDownloadPossible={isDownloadPossible}
                            isFavorite={photo.isFavorite}
                            isHidden={photo.isHidden}
                            isInCart={photo.isInCart}
                            isInView={isInView}
                            isLabeled={photo.tags.length > 0}
                            isPackageBuilderOpen={isPackageBuilderOpen}
                            isPreviewActive={isPreviewActive(photo.id, isOnScreen)}
                            isSlideshowPreviewActive={isSlideshowHoverPreviewActive({
                                allowsHoverPreview,
                                hoveredPhotoId: hoveredPreviewPhotoId,
                                photoId: photo.id,
                                photoType: photo.type,
                                prefersReducedMotion
                            })}
                            isReadyToCompare={isReadyToCompare}
                            isSelected={isPhotoSelected(photo)}
                            onClickPhoto={onClickPhoto}
                            onPreviewEnter={handlePreviewEnter}
                            onPreviewLeave={handlePreviewLeave}
                            photo={photo}
                            photoActions={photoActions}
                            photoDimensions={photoDimensions}
                            requiresDownloadDestination={
                                requiredDestinationSelectionPhotoId === photo.id
                            }
                            spFreeDigitals={spFreeDigitals}
                            translateFilter={translateFilter}
                            $rootScope={$rootScope}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export default PhotoGrid;
